import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import CheckBox from '@app/components/CheckBox'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import ListingStatusSelector from '@app/components/ListingStatusSelector'
import ListingTypeBox from '@app/components/ListingTypeBox'
import ListingTypeSelector from '@app/components/ListingTypeSelector'
import PropertyTypeSelector from '@app/components/PropertyTypeSelector'
import SearchBar from '@app/components/SearchBar'
import { PropertyType } from '@app/models/enums'
import { IListing, Listing } from '@app/models/Listing'
import {
  IGlobalConfigurationState,
  IGlobalHbUserState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import {
  formatAddressSingleLine,
  formatDate,
  humanize,
} from '@app/utils/formatting'
import { List } from 'immutable'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label, Nav, NavItem, NavLink } from 'reactstrap'
import {
  actionCreators,
  IListingsListState,
  ListingsScope,
} from '../modules/listingsList'

type ComponentProps = IListingsListState &
  IGlobalConfigurationState &
  IGlobalHbUserState &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class ListingsListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }
  }

  public render() {
    return (
      <DocumentTitle title='Listings'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>Listings</h3>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Type</Label>
                  <ListingTypeSelector
                    bsSize='sm'
                    includeAll
                    value={this.props.listingType}
                    onChange={this._onChangeListingType}
                  />
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Status</Label>
                  <ListingStatusSelector
                    bsSize='sm'
                    includeAll
                    value={this.props.status}
                    onChange={this._onChangeStatus}
                  />
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Primary Agent</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.primaryAgent}
                    onChange={this._onChangePrimaryAgent}
                  >
                    <option key='user1' value='All'>
                      All
                    </option>
                    {this.props.hbContacts
                      .map((c: any, i) => (
                        <option key={`user-${i}`} value={c.contactId}>
                          {c.name}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.listingSortOptions
                      .map((c, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={3}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
              <Col md={8} xl={6}>
                <FormGroup>
                  <Label>Property Type</Label>
                  <PropertyTypeSelector
                    value={this.props.propertyTypes}
                    onChange={this._onChangePropertyTypes}
                  />
                </FormGroup>
              </Col>
            </AdvancedSearch>
            <CheckBox
              onChange={(e) => {
                this.props.setOnlyNoPhotos(e)
                this._reloadData()
              }}
              value={this.props.onlyNoPhotos}
            >
              Only show listings without a photo
            </CheckBox>
            <div className='mb-3 mt-5'>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    active={this.props.scope === ListingsScope.Mine}
                    onClick={() => this._onChangeScope(ListingsScope.Mine)}
                  >
                    {ListingsScope.Mine}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={this.props.scope === ListingsScope.All}
                    onClick={() => this._onChangeScope(ListingsScope.All)}
                  >
                    {ListingsScope.All}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadListings}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const listing = rowData as IListing

                  return (
                    <ListingTypeBox
                      status={listing.status}
                      type={listing.type}
                    />
                  )
                }}
                dataKey='type'
                width={50}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const listing = rowData as IListing
                  return (
                    <div>
                      <div
                        className='overflow-text'
                        style={{ fontWeight: 'bold' }}
                      >
                        {listing.title}
                      </div>
                      <div className='overflow-text'>
                        {formatAddressSingleLine(
                          listing.associatedProperty.address,
                        )}
                      </div>
                    </div>
                  )
                }}
                dataKey='property'
                flexGrow={1}
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const listing = rowData as IListing
                  return listing.listingAgents.size > 0
                    ? listing.listingAgents.first().name
                    : ''
                }}
                dataKey='agentName'
                label='Primary Agent'
                width={150}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const listing = rowData as IListing
                  return listing.expirationDate
                    ? formatDate(listing.expirationDate, 'MM/DD/YYYY')
                    : 'No Expiration'
                }}
                dataKey='expirationDate'
                headerClassName='d-none d-lg-block'
                className='d-none d-lg-block'
                label='Expiration Date'
                width={120}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const listing = rowData as IListing
                  return Listing.getListingPrice(listing)
                }}
                dataKey='listingPrice'
                headerClassName='d-none d-lg-block'
                className='d-none d-lg-block'
                label='Listing Price'
                width={150}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No listings</div>
  }

  private _onRowClicked(item: IListing) {
    window.open(
      `${window.location.pathname.replace('listings', 'listing')}/${item.id}`,
    )
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeListingType(newListingType: string) {
    this.props.setListingType(newListingType)
    this._reloadData()
  }

  private _onChangePrimaryAgent(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setPrimaryAgent(event.target.value)
    this._reloadData()
  }

  private _onChangePropertyTypes(value: List<PropertyType>) {
    this.props.setPropertyTypes(value)
    this._reloadData()
  }

  private _onChangeScope(newScope: ListingsScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _onChangeStatus(newStatus: string) {
    this.props.setStatus(newStatus)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
